// Theme preference — the web half of the mobile app's light/dark/system setting.
//
// The web app shipped light-only. It now defaults to following the OS and lets
// the user override that from Settings; the choice is device-local, so it
// applies before any network call and survives sign-out.
//
// Note the split of responsibilities with the inline boot script in
// index.html: that script stamps the correct class on <html> before first
// paint (React mounts too late to avoid a flash of light). This store owns the
// preference afterwards and must apply the theme the same way the boot script
// does — keep THEME_PREFERENCE_STORAGE_KEY and the class name in sync with it.
import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_PREFERENCE_STORAGE_KEY = 'cranesafe.theme-preference';

const VALID_PREFERENCES: ThemePreference[] = ['light', 'dark', 'system'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (VALID_PREFERENCES as string[]).includes(value);
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches === true;
}

/** The theme actually rendered — `system` resolved against the OS setting. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

/** Stamp the resolved theme onto <html>. Tailwind is class-based (see
 *  tailwind.config.ts), and `color-scheme` makes native form controls,
 *  scrollbars and the browser chrome follow along. */
function applyPreference(preference: ThemePreference) {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(preference);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
}

interface ThemeState {
  preference: ThemePreference;
  /** The theme currently on screen. Components that need the *rendered* theme
   *  (chart palettes, canvas, inline SVG fills) should read this, not
   *  `preference` — `system` tells them nothing on its own. */
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
  /** Read the stored preference and start following the OS. Returns a cleanup
   *  function that detaches the media-query listener. */
  init: () => () => void;
}

function readStored(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    if (isThemePreference(stored)) return stored;
  } catch {
    // Storage can be unreadable (private mode, blocked cookies). Not a
    // failure — fall through to `system`.
  }
  return 'system';
}

const initialPreference = readStored();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  resolved: resolveTheme(initialPreference),

  setPreference: (preference) => {
    applyPreference(preference);
    set({ preference, resolved: resolveTheme(preference) });
    // Persisting is best-effort: a failed write must not stop the theme the
    // user just picked from taking effect on this run.
    try {
      localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
    } catch {
      /* empty */
    }
  },

  init: () => {
    const preference = readStored();
    applyPreference(preference);
    set({ preference, resolved: resolveTheme(preference) });

    if (typeof window === 'undefined' || !window.matchMedia) return () => {};

    // Unlike a phone, a browser tab stays open while the OS setting flips, so
    // `system` has to keep listening rather than resolve once at startup.
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => {
      if (get().preference !== 'system') return;
      applyPreference('system');
      set({ resolved: resolveTheme('system') });
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  },
}));
